/* eslint-disable no-useless-constructor */
/* global DeferredMedia */

// 3D product media. In a gallery the model spins slowly and takes no drags, so swiping
// still changes photos (a draggable model inside a swipeable gallery fought it on phones).
// Tapping it opens a full-screen viewer where the model can be turned freely and the
// "Show dimensions" box lives. AgModel3D is shared by the standard product gallery
// (product-model below) and the product builder gallery (assets/agpb.js).
window.AgModel3D = window.AgModel3D || {
  viewers: {},

  // Shopify's model-viewer build (1.12). On main-product Shopify loads it for the
  // model_viewer_tag; the builder gallery renders no such tag, so ask for it by name too.
  // The model-viewer-ui itself is not used: galleries are display only and the
  // full-screen viewer has its own controls.
  ready(cb) {
    customElements.whenDefined('model-viewer').then(() => cb());
    if (customElements.get('model-viewer') || this.loading) return;
    this.loading = true;
    Shopify.loadFeatures([
      { name: 'model-viewer-ui', version: '1.0', onLoad: () => {} },
      { name: 'model-viewer', version: '1.12', onLoad: () => {} }
    ]);
  },

  // turn a model-viewer into the slow-spinning, input-free gallery copy
  makeSpinner(viewer) {
    viewer.removeAttribute('camera-controls');
    viewer.setAttribute('auto-rotate', '');
    viewer.setAttribute('auto-rotate-delay', '0');
    viewer.setAttribute('rotation-per-second', '18deg');
    viewer.setAttribute('interaction-prompt', 'none');
    viewer.setAttribute('tabindex', '-1');
    viewer.classList.add('agm3d__gallery-viewer');
    return viewer;
  },

  // the "Tap to turn in 3D" control laid over a gallery slide
  makeOpener(model, label) {
    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'agm3d__open';
    open.innerHTML = '<span class="agm3d__open-label">Tap to turn in 3D</span>';
    open.setAttribute('aria-label', `Open the 3D view: ${label || 'product'}`);
    open.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.open(model, open);
    });
    return open;
  },

  // model: { src, poster, alt }
  open(model, opener) {
    if (!model || !model.src) return;
    this.ready(() => {
      if (!this.viewers[model.src]) this.viewers[model.src] = this.buildModal(model);
      const { modal, viewer } = this.viewers[model.src];
      this.current = { modal, opener };
      modal.hidden = false;
      document.documentElement.classList.add('agm3d-open');
      // start each visit from the front, like the gallery
      viewer.cameraOrbit = 'auto auto auto';
      viewer.jumpCameraToGoal?.();
      modal.querySelector('.agm3d__close').focus();
    });
  },

  close() {
    if (!this.current) return;
    this.current.modal.hidden = true;
    document.documentElement.classList.remove('agm3d-open');
    if (this.current.opener) this.current.opener.focus({ preventScroll: true });
    this.current = null;
  },

  buildModal(model) {
    const modal = document.createElement('div');
    modal.className = 'agm3d';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', model.alt || '3D view');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'agm3d__close';
    close.setAttribute('aria-label', 'Close the 3D view');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => this.close());

    const hint = document.createElement('p');
    hint.className = 'agm3d__hint';
    hint.textContent = 'Drag to turn. Pinch or scroll to zoom.';

    const viewer = document.createElement('model-viewer');
    viewer.className = 'agm3d__viewer';
    viewer.setAttribute('src', model.src);
    if (model.poster) viewer.setAttribute('poster', model.poster);
    viewer.setAttribute('alt', model.alt || '');
    viewer.setAttribute('camera-controls', '');
    viewer.setAttribute('auto-rotate', '');
    viewer.setAttribute('auto-rotate-delay', '3000');
    viewer.setAttribute('rotation-per-second', '18deg');
    viewer.setAttribute('interaction-prompt', 'none');
    viewer.setAttribute('shadow-intensity', '0.8');

    modal.append(viewer, close, hint);
    modal.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') this.close();
    });
    document.body.appendChild(modal);
    window.AgModelDimensions.attach(viewer);
    return { modal, viewer };
  }
};

if (!customElements.get('product-model') && typeof DeferredMedia !== 'undefined') {
  class ProductModel extends DeferredMedia {
    constructor() {
      super();
    }

    loadContent(focus = true, pause = true, loadTrigger = 'click') {
      const firstLoad = this.getAttribute('loaded') === null;
      // never focus the gallery copy: it takes no input, and focusing can scroll the gallery
      super.loadContent(false, pause, loadTrigger);
      if (!firstLoad) return;
      window.AgModel3D.ready(this.setupGalleryModel.bind(this));
    }

    setupGalleryModel() {
      const viewer = this.querySelector('model-viewer');
      if (!viewer) return;
      window.AgModel3D.makeSpinner(viewer);
      // the still preview picture sits under the (see-through) model; hide it once the model shows
      const ready = () => this.classList.add('agm3d--ready');
      if (viewer.loaded) ready(); else viewer.addEventListener('load', ready, { once: true });

      const model = { src: viewer.getAttribute('src'), poster: viewer.getAttribute('poster'), alt: viewer.getAttribute('alt') };
      this.appendChild(window.AgModel3D.makeOpener(model, model.alt));
    }
  }

  customElements.define('product-model', ProductModel);
}

// "Show dimensions" toggle on 3D product media: draws the model's overall box faintly and
// highlights width, depth and height with their sizes in mm. Sizes come from the model file,
// so any product with a correctly scaled 3D model gets them with no extra setup.
window.AgModelDimensions = window.AgModelDimensions || {
  attach(viewer) {
    if (!viewer || viewer.agDims) return;
    viewer.agDims = true;
    const setup = () => {
      if (viewer.agDimsReady) return;
      // Shopify's viewer (model-viewer 1.12) has getDimensions but not queryHotspot or
      // getBoundingBoxCenter, so only rely on getDimensions and read positions from the page
      if (typeof viewer.getDimensions !== 'function') return;
      viewer.agDimsReady = true;
      this.build(viewer);
    };
    // the load event can fire before we listen, so check once the element is defined too
    viewer.addEventListener('load', setup, { once: true });
    customElements.whenDefined('model-viewer').then(() => { if (viewer.loaded) setup(); });
  },

  build(viewer) {
    const NS = 'http://www.w3.org/2000/svg';
    // with no camera-target set and before any interaction, the camera target is the box centre
    const c = typeof viewer.getBoundingBoxCenter === 'function' ? viewer.getBoundingBoxCenter() : viewer.getCameraTarget();
    const s = viewer.getDimensions();
    const X = [c.x - s.x / 2, c.x + s.x / 2];
    const Y = [c.y - s.y / 2, c.y + s.y / 2];
    const Z = [c.z - s.z / 2, c.z + s.z / 2];
    const at = (x, y, z) => `${x}m ${y}m ${z}m`;
    const mm = (v) => `${Math.round(v * 1000)} mm`;
    const uid = Math.random().toString(36).slice(2, 8);
    const spot = (name, cls, pos, text) => {
      const el = document.createElement('div');
      el.slot = `hotspot-agdim-${uid}-${name}`;
      el.className = cls;
      el.dataset.position = pos;
      if (text) el.textContent = text;
      viewer.appendChild(el);
      return el;
    };

    viewer.classList.add('agdim', 'agdim--off');
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'agdim__lines');
    svg.setAttribute('aria-hidden', 'true');
    viewer.appendChild(svg);

    // corner k: bit 1 = x max, bit 2 = y max, bit 4 = z max (z max is the model's front)
    const corners = [];
    for (let k = 0; k < 8; k++) corners.push(spot(`c${k}`, 'agdim__anchor', at(X[k & 1], Y[(k >> 1) & 1], Z[(k >> 2) & 1])));
    // measured: width on the front floor edge, depth on the right floor edge, height up the front right corner
    const key = ['4-5', '1-5', '5-7'];
    const lines = [];
    for (let k = 0; k < 8; k++) {
      [1, 2, 4].forEach((b) => {
        if (k & b) return;
        const line = document.createElementNS(NS, 'line');
        line.setAttribute('class', key.includes(`${k}-${k | b}`) ? 'agdim__key' : 'agdim__box');
        svg.appendChild(line);
        lines.push([k, k | b, line]);
      });
    }
    spot('w', 'agdim__label', at(c.x, Y[0], Z[1]), mm(s.x));
    spot('d', 'agdim__label', at(X[1], Y[0], c.z), mm(s.z));
    spot('h', 'agdim__label', at(X[1], c.y, Z[1]), mm(s.y));

    // model-viewer places each corner marker on screen; join the markers' centres
    const draw = () => {
      const box = svg.getBoundingClientRect();
      const pt = corners.map((el) => {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top };
      });
      lines.forEach(([a, b, line]) => {
        if (!pt[a] || !pt[b]) return;
        line.setAttribute('x1', pt[a].x);
        line.setAttribute('y1', pt[a].y);
        line.setAttribute('x2', pt[b].x);
        line.setAttribute('y2', pt[b].y);
      });
    };
    // redraw every frame while shown: hotspot screen positions settle a frame after each camera move
    const loop = () => {
      if (viewer.classList.contains('agdim--off')) return;
      draw();
      requestAnimationFrame(loop);
    };

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'agdim__toggle';
    btn.setAttribute('aria-pressed', 'false');
    btn.textContent = 'Show dimensions';
    btn.addEventListener('click', (evt) => {
      evt.stopPropagation();
      const on = viewer.classList.toggle('agdim--off') === false;
      btn.setAttribute('aria-pressed', on);
      btn.textContent = on ? 'Hide dimensions' : 'Show dimensions';
      if (on) requestAnimationFrame(loop);
    });
    viewer.appendChild(btn);
  }
};

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this)
      }
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
      return;
    }

    document.querySelectorAll('[id^="product-json-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });

    window.ShopifyXR.setupXRElements();
  }
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel) window.ProductModel.loadShopifyXR();
});
